import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import GeoArt from "../components/GeoArt";
import Marquee from "../components/Marquee";
import { MaskedLines, Reveal, SectionHead } from "../components/Reveal";

const IMPACT = [
  ["4+", "Annual Exhibitions"],
  ["2022", "Founded at Tuskegee"],
  ["100%", "Proceeds to Students"],
  ["40+", "Artists Featured"],
];

const ARTISTS = ["Atuarra McCaslin", "Guest Artists from the Tuskegee Community", "Alumni & Faculty Artists", "Emerging Undergraduate Artists"];

export default function Rise() {
  return (
    <main className="pt-24 sm:pt-32" data-testid="rise-page">
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 opacity-25 grid grid-cols-3">
          {["rise-h1", "rise-h2", "rise-h3"].map((s) => (
            <GeoArt key={s} seed={s} className="w-full h-full" />
          ))}
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 to-background" />
        <div className="relative container-x section-y">
          <p className="eyebrow mb-6" data-testid="rise-eyebrow">RISE Art Show — Tuskegee University Homecoming</p>
          <MaskedLines lines={["Art For", "Impact"]} className="h-display !text-6xl sm:!text-8xl lg:!text-9xl text-[#FF1A1A]" />
          <Reveal delay={0.35}>
            <p className="mt-10 max-w-2xl text-muted-foreground" data-testid="rise-description">
              RISE is an annual fundraising exhibition held during Tuskegee University Homecoming. Through the creative arts, the exhibition raises funding and support for undergraduate students while celebrating artists connected to the Tuskegee community.
            </p>
          </Reveal>
          <Reveal delay={0.5}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/collect?collection=RISE Collection" className="btn-red" data-testid="rise-support-cta">Support RISE <ArrowRight size={16} /></Link>
              <Link to="/contact?category=RISE Art Show" className="btn-ghost" data-testid="rise-participate-cta">Participate as an Artist</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee items={["RISE 2022", "RISE 2023", "RISE 2024", "RISE 2025", "Tuskegee Homecoming", "Art For Impact", "Fund the Future"]} />

      <section className="border-b border-border">
        <div className="container-x section-y grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border" data-testid="rise-impact">
          {IMPACT.map(([num, label], i) => (
            <Reveal key={label} delay={i * 0.06} className="bg-card">
              <div className="p-8 text-center">
                <p className="font-display font-black text-4xl sm:text-5xl text-[#FF1A1A]">{num}</p>
                <p className="font-mono-x text-[10px] uppercase tracking-widest text-muted-foreground mt-3">{label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="container-x section-y grid grid-cols-1 lg:grid-cols-2 gap-12">
        <Reveal>
          <div>
            <p className="font-mono-x text-xs text-[#FF1A1A] mb-4">MISSION</p>
            <h2 className="h-section mb-6" data-testid="rise-mission-title">Turn Creativity Into Opportunity</h2>
            <p className="text-muted-foreground max-w-md">
              RISE exists so that art does more than hang on a wall. Every exhibition converts culture into funding, mentorship, and visibility for undergraduate students — while giving artists connected to Tuskegee a serious platform during one of the most electric weekends of the year.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="card-line p-8 sm:p-10">
            <p className="font-mono-x text-xs text-[#FF1A1A] mb-4">FOUNDER, CURATOR & PARTICIPATING ARTIST</p>
            <h3 className="font-display font-black uppercase text-2xl mb-4" data-testid="rise-founder">Atuarra McCaslin</h3>
            <p className="text-sm text-muted-foreground mb-8">
              Atuarra founded RISE in 2022 and has curated every edition since — building it from a homecoming idea into an annual institution that students, alumni, and collectors plan their year around.
            </p>
            <SectionHead eyebrow="Participating Artists" title="" className="!mb-4" />
            <ul className="space-y-3" data-testid="rise-artists-list">
              {ARTISTS.map((a) => (
                <li key={a} className="flex items-center gap-3 text-sm border-b border-border pb-3">
                  <span className="w-1.5 h-1.5 bg-[#FF1A1A] rotate-45 shrink-0" /> {a}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      <section className="border-t border-border">
        <div className="container-x section-y">
          <SectionHead eyebrow="Past Exhibitions" title="Four Years of RISE" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {["2022", "2023", "2024", "2025"].map((y, i) => (
              <Reveal key={y} delay={i * 0.06}>
                <div className="border border-border overflow-hidden group" data-testid={`rise-edition-${y}`}>
                  <div className="transition-transform duration-700 group-hover:scale-105">
                    <GeoArt seed={`rise-${y}`} ratio="1/1" className="w-full" />
                  </div>
                  <div className="p-5 border-t border-border">
                    <p className="font-display font-extrabold uppercase text-lg">RISE {y}</p>
                    <p className="font-mono-x text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Tuskegee Homecoming</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
